import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, addDoc, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import ConfirmationDialog from '../components/ConfirmationDialog';
import './RsvpPage.css';

function RsvpPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    attending: 0,
    declined: 0,
    total: 0,
    additionalGuests: 0
  });

  // For adding new guests
  const [showAddGuestForm, setShowAddGuestForm] = useState(false);
  const [newGuest, setNewGuest] = useState({
    name: '',
    email: '',
    response: 'pending',
    guestCount: 1,
    message: ''
  });

  // Add dialog state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [guestToDelete, setGuestToDelete] = useState(null);

  // Load user's events and guests
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      try {
        setLoading(true);
        
        // Get all events for this user
        const eventsRef = collection(db, 'events');
        const q = query(eventsRef, where('userId', '==', currentUser.id));
        const eventsSnapshot = await getDocs(q);
        
        const userEvents = eventsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        console.log("Found events:", userEvents.length);
        setEvents(userEvents);
        
        // Set first event as selected if available
        if (userEvents.length > 0) {
          setSelectedEvent(userEvents[0]);
          await loadGuestsForEvent(userEvents[0].id);
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error loading RSVP data:", error);
        setError("Failed to load events. Please try again later.");
        setLoading(false);
      }
    };
    
    loadData();
  }, [currentUser, navigate]);
  
  // Function to load guests for a specific event
  const loadGuestsForEvent = async (eventId) => {
    try {
      setLoading(true);
      
      // Get all guests for this event
      const guestsRef = collection(db, 'guests');
      const q = query(guestsRef, where('eventId', '==', eventId));
      const guestsSnapshot = await getDocs(q);
      
      const eventGuests = guestsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setGuests(eventGuests);
      
      // Calculate statistics
      const attending = eventGuests.filter(g => g.response === 'attending').length;
      const declined = eventGuests.filter(g => g.response === 'declined').length;
      const pending = eventGuests.filter(g => g.response === 'pending').length;
      
      // Calculate total guests by summing up the guestCount field
      const totalGuestCount = eventGuests.reduce((total, guest) => {
        // Use the guestCount field directly, fallback to 1 if not available
        return total + (parseInt(guest.guestCount) || 1);
      }, 0);
      
      // Calculate attending guests (using guestCount for attending guests)
      const attendingWithGuests = eventGuests.reduce((total, guest) => {
        if (guest.response === 'attending') {
          return total + (parseInt(guest.guestCount) || 1);
        }
        return total;
      }, 0);
      
      setStats({
        attending,
        declined,
        pending,
        total: totalGuestCount,
        attendingWithGuests: attendingWithGuests
      });
      
      setLoading(false);
    } catch (error) {
      console.error("Error loading guests:", error);
      setError("Failed to load guest list. Please try again.");
      setLoading(false);
    }
  };
  
  // Handle event selection change
  const handleEventChange = (eventId) => {
    const event = events.find(e => e.id === eventId);
    if (event) {
      setSelectedEvent(event);
      loadGuestsForEvent(eventId);
    }
  };
  
  // Handle adding a new guest
  const handleAddGuest = async (e) => {
    e.preventDefault();
    
    if (!selectedEvent) {
      setError("Please select an event first");
      return;
    }
    
    if (!newGuest.name || !newGuest.email) {
      setError("Please fill in all required fields");
      return;
    }
    
    try {
      setLoading(true);
      
      const guestData = {
        ...newGuest,
        eventId: selectedEvent.id,
        createdAt: new Date().toISOString()
      };
      
      const guestsRef = collection(db, 'guests');
      await addDoc(guestsRef, guestData);
      
      // Reset form
      setNewGuest({
        name: '',
        email: '',
        response: 'pending',
        guestCount: 1,
        message: ''
      });
      
      setShowAddGuestForm(false);
      
      // Reload guests for this event
      await loadGuestsForEvent(selectedEvent.id);
      
      setLoading(false);
    } catch (error) {
      console.error("Error adding guest:", error);
      setError("Failed to add guest. Please try again.");
      setLoading(false);
    }
  };
  
  // Handle updating guest response
  const handleUpdateGuest = async (guestId, response) => {
    try {
      setLoading(true);
      
      const guestRef = doc(db, 'guests', guestId);
      await updateDoc(guestRef, { response });
      
      // Reload guests
      await loadGuestsForEvent(selectedEvent.id);
      
      setLoading(false);
    } catch (error) {
      console.error("Error updating guest:", error);
      setError("Failed to update guest status. Please try again.");
      setLoading(false);
    }
  };
  
  // Handle deleting a guest
  const handleDeleteGuest = async (guestId) => {
    setGuestToDelete(guestId);
    setIsDeleteDialogOpen(true);
  };
  
  const confirmDeleteGuest = async () => {
    try {
      setLoading(true);
      
      const guestRef = doc(db, 'guests', guestToDelete);
      await deleteDoc(guestRef);
      
      // Update the list by removing the deleted guest
      setGuests(guests.filter(guest => guest.id !== guestToDelete));
      
      setLoading(false);
      setIsDeleteDialogOpen(false);
      setGuestToDelete(null);
    } catch (error) {
      console.error("Error deleting guest:", error);
      setError("Failed to delete guest. Please try again.");
      setLoading(false);
      
      // Close the dialog
      setIsDeleteDialogOpen(false);
      setGuestToDelete(null);
    }
  };
  
  const cancelDeleteGuest = () => {
    // Close the dialog without deleting
    setIsDeleteDialogOpen(false);
    setGuestToDelete(null);
  };
  
  // Show loading state
  if (loading && !selectedEvent) {
    return <div className="rsvp-loading">Loading RSVP data...</div>;
  }
  
  return (
    <div className="rsvp-page">
      <Header />
      
      <div className="rsvp-container">
        <h1>RSVP List</h1>
        
        {error && (
          <div className="error-message">{error}</div>
        )}
        
        {events.length > 0 ? (
          <>
            <div className="event-selector">
              <label htmlFor="event-select">Select Event</label>
              <select 
                id="event-select" 
                value={selectedEvent?.id || ''}
                onChange={(e) => handleEventChange(e.target.value)}
                className="event-select"
              >
                {events.map(event => (
                  <option key={event.id} value={event.id}>
                    {event.name}
                  </option>
                ))}
              </select>
            </div>
            
            {selectedEvent && (
              <div className="event-rsvp-container">
                <div className="rsvp-actions">
                  <button 
                    className="add-guest-btn"
                    onClick={() => setShowAddGuestForm(!showAddGuestForm)}
                  >
                    {showAddGuestForm ? 'Cancel' : '+ Add Guest'}
                  </button>
                  
                  <button 
                    className="refresh-btn"
                    onClick={() => loadGuestsForEvent(selectedEvent.id)}
                    disabled={loading}
                  >
                    ↻ Refresh
                  </button>
                </div>
                
                {showAddGuestForm && (
                  <div className="add-guest-form">
                    <h3>Add New Guest</h3>
                    <form onSubmit={handleAddGuest}>
                      <div className="form-group">
                        <label htmlFor="guest-name">Name</label>
                        <input
                          id="guest-name"
                          type="text"
                          value={newGuest.name}
                          onChange={(e) => setNewGuest({...newGuest, name: e.target.value})}
                          required
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="guest-email">Email</label>
                        <input
                          id="guest-email"
                          type="email"
                          value={newGuest.email}
                          onChange={(e) => setNewGuest({...newGuest, email: e.target.value})}
                          required
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="guest-status">Status</label>
                        <select
                          id="guest-status"
                          value={newGuest.response}
                          onChange={(e) => setNewGuest({...newGuest, response: e.target.value})}
                        >
                          <option value="pending">Pending</option>
                          <option value="attending">Attending</option>
                          <option value="declined">Declined</option>
                        </select>
                      </div>
                      
                      {newGuest.response === 'attending' && (
                        <div className="form-group">
                          <label htmlFor="additional-guests">Guest Count</label>
                          <select
                            id="guest-count"
                            value={newGuest.guestCount || 1}
                            onChange={(e) => setNewGuest({...newGuest, guestCount: e.target.value})}
                          >
                            <option value="1">1</option>
                            <option value="2">2</option>
                            <option value="3">3</option>
                            <option value="4">4</option>
                            <option value="5">5</option>
                          </select>
                        </div>
                      )}
                      
                      <div className="form-group">
                        <label htmlFor="guest-message">Message</label>
                        <textarea
                          id="guest-message"
                          value={newGuest.message}
                          onChange={(e) => setNewGuest({...newGuest, message: e.target.value})}
                        ></textarea>
                      </div>
                      
                      <div className="form-actions">
                        <button 
                          type="button" 
                          className="cancel-btn"
                          onClick={() => setShowAddGuestForm(false)}
                        >
                          Cancel
                        </button>
                        <button 
                          type="submit" 
                          className="submit-btn"
                          disabled={loading}
                        >
                          Add Guest
                        </button>
                      </div>
                    </form>
                  </div>
                )}
                
                <div className="rsvp-stats">
                  <div className="stat-card">
                    <span className="stat-value">{guests.length}</span>
                    <span className="stat-label">RSVP Count</span>
                  </div>
                  <div className="stat-card attending">
                    <span className="stat-value">{stats.total}</span>
                    <span className="stat-label">Total Guests</span>
                  </div>
                  <div className="stat-card total">
                    <span className="stat-value">{stats.attendingWithGuests}</span>
                    <span className="stat-label">Attending</span>
                  </div>
                </div>
                
                <div className="guest-list-container">
                  <h2>Guest List</h2>
                  
                  {loading ? (
                    <div className="loading-spinner">Loading guest list...</div>
                  ) : guests.length > 0 ? (
                    <div className="guest-list">
                      <table>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Status</th>
                            <th>Guest Count</th>
                            <th>Message</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {guests.map(guest => (
                            <tr key={guest.id} className={`status-${guest.response}`}>
                              <td>{guest.name}</td>
                              <td>{guest.email}</td>
                              <td>
                                <select
                                  value={guest.response}
                                  onChange={(e) => handleUpdateGuest(guest.id, e.target.value)}
                                  className={`status-select ${guest.response}`}
                                >
                                  <option value="pending">Pending</option>
                                  <option value="attending">Attending</option>
                                  <option value="declined">Declined</option>
                                </select>
                              </td>
                              <td>{guest.guestCount || 1}</td>
                              <td className="guest-message">{guest.message}</td>
                              <td>
                                <button 
                                  className="delete-guest-btn"
                                  onClick={() => handleDeleteGuest(guest.id)}
                                  title="Delete guest"
                                >
                                  ×
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="no-guests-message">
                      No RSVPs received yet.
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="no-events-message">
            <p>You don't have any events yet.</p>
            <button onClick={() => navigate('/events/new')}>
              Create Your First Event
            </button>
          </div>
        )}
      </div>
      
      {/* Add the ConfirmationDialog component */}
      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        title="Remove Guest"
        message="Are you sure you want to remove this guest? This action cannot be undone."
        confirmText="Remove"
        cancelText="Cancel"
        onConfirm={confirmDeleteGuest}
        onCancel={cancelDeleteGuest}
        type="delete"
      />
    </div>
  );
}

export default RsvpPage; 